import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useWebSocket } from './useWebSocket';
import { useTelnyxWebRTC, type WebRTCCredentials as TelnyxWebRTCCredentials } from './useTelnyxWebRTC';
import { dialerService, type Call, type CallParticipant, type RecentCall, type QuickDialContact, type IncomingCall, type AnswerIncomingResponse } from '../services/dialer';
import { settingsService } from '../services/settings';

export type CallStatus = 'idle' | 'calling' | 'ringing' | 'connected' | 'on_hold' | 'transferring' | 'ended';
export type ConnectionMode = 'webrtc' | 'phone';
export type OfficerStatus = 'available' | 'busy' | 'away' | 'offline';

// Storage key for persisting settings
const DIALER_SETTINGS_KEY = 'adaptcx_dialer_settings';

interface DialerSettings {
  connectionMode: ConnectionMode;
  forwardToNumber: string;
}

function loadSettings(): DialerSettings {
  try {
    const saved = localStorage.getItem(DIALER_SETTINGS_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch {
    // Ignore parse errors
  }
  return { connectionMode: 'webrtc', forwardToNumber: '' };
}

function saveSettings(settings: DialerSettings) {
  try {
    localStorage.setItem(DIALER_SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Ignore storage errors
  }
}

// ============================================================================
// SHARED STATE (module-level singleton - shared across all components)
// ============================================================================
const savedSettings = loadSettings();

// State
const phoneNumber = ref('');
const callStatus = ref<CallStatus>('idle');
const connectionMode = ref<ConnectionMode>(savedSettings.connectionMode);
const officerStatus = ref<OfficerStatus>('available');
const forwardToNumber = ref(savedSettings.forwardToNumber);

// Current call
const currentCall = ref<Call | null>(null);
const participants = ref<CallParticipant[]>([]);
const callDuration = ref(0);
const callDurationInterval = ref<ReturnType<typeof setInterval> | null>(null);

// Call controls
const isMuted = ref(false);
const isOnHold = ref(false);

// Incoming call state
const incomingCall = ref<IncomingCall | null>(null);
const isAnsweringIncoming = ref(false);
let ringtoneAudio: HTMLAudioElement | null = null;

// Ringtone URL - loaded from tenant settings, fallback to default
const DEFAULT_RINGTONE_URL = '';  // Configure via tenant settings
const ringtoneUrl = ref<string>(DEFAULT_RINGTONE_URL);

// Lists
const recentCalls = ref<RecentCall[]>([]);
const quickDialContacts = ref<QuickDialContact[]>([]);

// Loading states
const isLoading = ref(false);
const isLoadingRecent = ref(false);
const isLoadingQuickDial = ref(false);
const isWebRTCInitializing = ref(false);

// Error state
const error = ref<string | null>(null);

// WebRTC credentials
const webrtcCredentials = ref<TelnyxWebRTCCredentials | null>(null);

// ============================================================================

export function useDialer() {
  // WebSocket connection
  const { isConnected: wsConnected, onChannel } = useWebSocket();
  let unsubscribeCall: (() => void) | null = null;

  // Telnyx WebRTC (per-component instance, but state is shared)
  const telnyxWebRTC = useTelnyxWebRTC();

  // Computed
  const isInCall = computed(() =>
    ['calling', 'ringing', 'connected', 'on_hold', 'transferring'].includes(callStatus.value)
  );

  const hasIncomingCall = computed(() => incomingCall.value !== null);

  const formattedDuration = computed(() => {
    const mins = Math.floor(callDuration.value / 60);
    const secs = callDuration.value % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  });

  const canMakeCall = computed(() =>
    phoneNumber.value.length >= 3 && !isInCall.value && !isLoading.value
  );

  const isWebRTCReady = computed(() => telnyxWebRTC.isReady.value);

  // Persist settings on change
  watch([connectionMode, forwardToNumber], () => {
    saveSettings({
      connectionMode: connectionMode.value,
      forwardToNumber: forwardToNumber.value,
    });
  });

  // Sync WebRTC state with local state
  watch(() => telnyxWebRTC.state.value.callState, (newState) => {
    if (connectionMode.value !== 'webrtc') return;

    switch (newState) {
      case 'connecting':
        callStatus.value = 'calling';
        break;
      case 'ringing':
        callStatus.value = 'ringing';
        break;
      case 'active':
        callStatus.value = 'connected';
        isOnHold.value = false;
        // Update participant status to connected ONLY for incoming calls
        // For incoming calls, the PSTN caller is already connected when officer answers
        // For outbound calls, the target is still ringing when officer's WebRTC connects
        if (participants.value.length > 0 && currentCall.value?.direction === 'inbound') {
          participants.value = participants.value.map(p => ({
            ...p,
            status: 'connected' as const,
          }));
          console.log('[useDialer] WebRTC active (inbound) - updated participants to connected');
        }
        break;
      case 'held':
        callStatus.value = 'on_hold';
        isOnHold.value = true;
        break;
      case 'ended':
      case 'idle':
        if (callStatus.value !== 'idle') {
          resetCallState();
          loadRecentCalls();
        }
        break;
    }
  });

  watch(() => telnyxWebRTC.state.value.isMuted, (muted) => {
    if (connectionMode.value === 'webrtc') {
      isMuted.value = muted;
    }
  });

  watch(() => telnyxWebRTC.state.value.callDuration, (duration) => {
    if (connectionMode.value === 'webrtc') {
      callDuration.value = duration;
    }
  });

  // Format phone number for display
  function formatPhoneNumber(number: string): string {
    const digits = number.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    return `+${digits.slice(0, 1)} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 11)}`;
  }

  // Format phone number to E.164
  function toE164(number: string): string {
    const digits = number.replace(/\D/g, '');
    if (digits.length === 10) {
      return `+1${digits}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    }
    if (!number.startsWith('+')) {
      return `+${digits}`;
    }
    return number;
  }

  // Handle digit input
  function inputDigit(digit: string) {
    if (phoneNumber.value.length < 15) {
      phoneNumber.value += digit;

      // Send DTMF if in call
      if (isInCall.value) {
        sendDTMF(digit);
      }
    }
  }

  function backspace() {
    phoneNumber.value = phoneNumber.value.slice(0, -1);
  }

  function clearNumber() {
    phoneNumber.value = '';
  }

  // Send DTMF tone
  async function sendDTMF(digit: string) {
    if (connectionMode.value === 'webrtc') {
      telnyxWebRTC.sendDTMF(digit);
    } else if (currentCall.value) {
      try {
        await dialerService.sendDTMF(currentCall.value.id, digit);
      } catch (err) {
        console.error('Failed to send DTMF:', err);
      }
    }
  }

  // Initialize WebRTC if needed
  async function initializeWebRTC(): Promise<boolean> {
    if (telnyxWebRTC.state.value.isInitialized) {
      return true;
    }

    isWebRTCInitializing.value = true;
    error.value = null;

    try {
      // Get credentials from backend
      const credentials = await dialerService.getWebRTCCredentials();

      // Convert to format expected by useTelnyxWebRTC
      const telnyxCredentials: TelnyxWebRTCCredentials = {
        token: credentials.token,
        identity: credentials.identity,
        expires_at: credentials.expires_at,
        sip_uri: credentials.sip_uri || '',  // Officer's DID for conference-based calling
        ice_servers: credentials.ice_servers,
        caller_name: credentials.caller_name || undefined,
        caller_number: credentials.caller_number || undefined,
      };

      webrtcCredentials.value = telnyxCredentials;
      await telnyxWebRTC.initialize(telnyxCredentials);
      return true;
    } catch (err: any) {
      console.error('Failed to initialize WebRTC:', err);
      error.value = err.message || 'Failed to initialize WebRTC';
      return false;
    } finally {
      isWebRTCInitializing.value = false;
    }
  }

  // Initiate call
  async function initiateCall(numberToCall?: string) {
    const targetNumber = numberToCall || phoneNumber.value;
    if (!targetNumber || isInCall.value) return;

    isLoading.value = true;
    error.value = null;
    officerStatus.value = 'busy';

    try {
      if (connectionMode.value === 'webrtc') {
        // WebRTC mode - use Telnyx SDK
        await initiateWebRTCCall(targetNumber);
      } else {
        // Phone mode - use backend API
        await initiatePhoneCall(targetNumber);
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to initiate call';
      callStatus.value = 'idle';
      officerStatus.value = 'available';
      console.error('Failed to initiate call:', err);
    } finally {
      isLoading.value = false;
    }
  }

  // Initiate WebRTC call
  // Uses conference-based calling: WebRTC calls officer's SIP URI, webhook creates conference and dials customer
  async function initiateWebRTCCall(targetNumber: string) {
    // Initialize WebRTC if not already done
    const initialized = await initializeWebRTC();
    if (!initialized) {
      throw new Error('Failed to initialize WebRTC');
    }

    callStatus.value = 'calling';

    // Format target number to E.164
    const e164TargetNumber = toE164(targetNumber);

    // Add temp participant for the target - will be updated with real backend ID via WebSocket
    const tempId = `temp-${Date.now()}`;
    participants.value = [{
      id: tempId,
      call_id: '',
      name: 'Customer',
      number: e164TargetNumber,
      type: 'external' as const,
      status: 'ringing' as const,
      joined_at: new Date().toISOString(),
    }];
    console.log('[useDialer] Added temp participant:', participants.value[0]);

    // Get officer's SIP URI for conference-based calling
    const sipUri = webrtcCredentials.value?.sip_uri;

    if (sipUri) {
      // Conference-based calling: Call officer's own DID via SIP URI
      // Webhook will answer, create conference, and dial the customer
      console.log('[useDialer] Conference-based call via SIP URI:', sipUri, 'target:', e164TargetNumber);

      await telnyxWebRTC.call(sipUri, {
        callerName: webrtcCredentials.value?.caller_name,
        callerNumber: webrtcCredentials.value?.caller_number,
        // Pass target number in client_state for webhook to dial
        clientState: {
          target_number: e164TargetNumber,
          call_type: 'did_outbound',
          initiated_from: 'webrtc_dialer',
        },
      });
    } else {
      // Fallback: Direct call to target (no conference)
      console.log('[Dialer] Direct call to:', e164TargetNumber);

      await telnyxWebRTC.call(e164TargetNumber, {
        callerName: webrtcCredentials.value?.caller_name,
        callerNumber: webrtcCredentials.value?.caller_number,
      });
    }

    startDurationTimer();
  }

  // Initiate phone call via backend
  async function initiatePhoneCall(targetNumber: string) {
    if (!forwardToNumber.value) {
      throw new Error('Forward number required for phone mode. Configure in settings.');
    }

    callStatus.value = 'calling';

    const call = await dialerService.initiateCall({
      to_number: toE164(targetNumber),
      mode: 'phone',
      forward_to: toE164(forwardToNumber.value),
    });

    currentCall.value = call;
    startDurationTimer();
  }

  // End call
  async function endCall() {
    console.log('[useDialer] ===== END CALL =====');
    console.log('[useDialer] connectionMode:', connectionMode.value);
    console.log('[useDialer] currentCall:', currentCall.value);

    if (connectionMode.value === 'webrtc') {
      // For WebRTC dialer calls, we need to:
      // 1. End the conference via backend (drops all participants)
      // 2. Hangup the WebRTC connection locally
      if (currentCall.value) {
        try {
          // End conference - this drops all participants including customer
          console.log('[useDialer] Calling dialerService.endCall with id:', currentCall.value.id);
          console.log('[useDialer] conference_id:', currentCall.value.conference_id);
          await dialerService.endCall(currentCall.value.id);
          console.log('[useDialer] Backend endCall completed successfully');
        } catch (err) {
          console.error('[useDialer] Failed to end conference:', err);
        }
      } else {
        console.warn('[useDialer] No currentCall - backend conference will not be ended!');
        console.warn('[useDialer] Only local WebRTC connection will be hung up.');
      }
      // Also hangup the local WebRTC connection
      console.log('[useDialer] Hanging up local WebRTC connection...');
      telnyxWebRTC.hangup();
    } else if (currentCall.value) {
      try {
        console.log('[useDialer] Calling dialerService.endCall (phone mode)');
        await dialerService.endCall(currentCall.value.id);
      } catch (err) {
        console.error('[useDialer] Failed to end call:', err);
      }
    }
    resetCallState();
  }

  // Toggle mute
  async function toggleMute() {
    if (connectionMode.value === 'webrtc') {
      telnyxWebRTC.toggleMute();
    } else if (currentCall.value) {
      try {
        if (isMuted.value) {
          await dialerService.unmute(currentCall.value.id);
        } else {
          await dialerService.mute(currentCall.value.id);
        }
        isMuted.value = !isMuted.value;
      } catch (err) {
        console.error('Failed to toggle mute:', err);
      }
    }
  }

  // Toggle hold
  async function toggleHold() {
    if (connectionMode.value === 'webrtc') {
      telnyxWebRTC.toggleHold();
    } else if (currentCall.value) {
      try {
        if (isOnHold.value) {
          await dialerService.resume(currentCall.value.id);
          callStatus.value = 'connected';
        } else {
          await dialerService.hold(currentCall.value.id);
          callStatus.value = 'on_hold';
        }
        isOnHold.value = !isOnHold.value;
      } catch (err) {
        console.error('Failed to toggle hold:', err);
      }
    }
  }

  // Transfer call
  async function transferCall(targetNumber: string) {
    if (!currentCall.value && connectionMode.value !== 'webrtc') return;

    callStatus.value = 'transferring';

    try {
      if (connectionMode.value === 'phone' && currentCall.value) {
        await dialerService.transfer(currentCall.value.id, toE164(targetNumber));
      }
      // WebRTC transfer would need to be implemented in Telnyx SDK
      resetCallState();
    } catch (err) {
      error.value = 'Failed to transfer call';
      callStatus.value = 'connected';
      console.error('Failed to transfer call:', err);
    }
  }

  // Add participant to bridge (phone mode only)
  async function addParticipant(number: string) {
    if (!currentCall.value || connectionMode.value !== 'phone') return;

    try {
      const participant = await dialerService.addParticipant(currentCall.value.id, toE164(number));
      participants.value.push(participant);
    } catch (err) {
      error.value = 'Failed to add participant';
      console.error('Failed to add participant:', err);
    }
  }

  // Remove participant from bridge
  async function removeParticipant(participantId: string) {
    if (!currentCall.value) return;

    try {
      await dialerService.removeParticipant(currentCall.value.id, participantId);
      participants.value = participants.value.filter(p => p.id !== participantId);
    } catch (err) {
      console.error('Failed to remove participant:', err);
    }
  }

  // Load recent calls
  async function loadRecentCalls() {
    isLoadingRecent.value = true;
    try {
      recentCalls.value = await dialerService.getRecentCalls();
    } catch (err) {
      console.error('Failed to load recent calls:', err);
    } finally {
      isLoadingRecent.value = false;
    }
  }

  // Load quick dial contacts
  async function loadQuickDialContacts() {
    isLoadingQuickDial.value = true;
    try {
      quickDialContacts.value = await dialerService.getQuickDialContacts();
    } catch (err) {
      console.error('Failed to load quick dial contacts:', err);
    } finally {
      isLoadingQuickDial.value = false;
    }
  }

  // Add contact to quick dial
  async function addToQuickDial(contactId: string) {
    try {
      const added = await dialerService.addToQuickDial(contactId);
      quickDialContacts.value.push(added);
    } catch (err) {
      console.error('Failed to add to quick dial:', err);
      throw err;
    }
  }

  // Remove contact from quick dial
  async function removeFromQuickDial(contactId: string) {
    try {
      await dialerService.removeFromQuickDial(contactId);
      quickDialContacts.value = quickDialContacts.value.filter(c => c.id !== contactId);
    } catch (err) {
      console.error('Failed to remove from quick dial:', err);
      throw err;
    }
  }

  // Incoming call handling
  function playRingtone() {
    try {
      // Use tenant-configured ringtone URL, or fallback to default
      const audioUrl = ringtoneUrl.value || DEFAULT_RINGTONE_URL;

      // Recreate audio element if URL changed or doesn't exist
      if (!ringtoneAudio || ringtoneAudio.src !== audioUrl) {
        ringtoneAudio = new Audio(audioUrl);
        ringtoneAudio.loop = true;
      }
      ringtoneAudio.currentTime = 0;
      ringtoneAudio.play().catch(err => {
        // Browser autoplay policy blocks audio until user interaction
        // This is expected behavior - the visual notification will still show
        if (err.name === 'NotAllowedError') {
          console.log('[useDialer] Ringtone blocked by autoplay policy - visual notification only');
        } else {
          console.warn('[useDialer] Failed to play ringtone:', err);
        }
      });
    } catch (err) {
      console.warn('[useDialer] Failed to initialize ringtone:', err);
    }
  }

  function stopRingtone() {
    if (ringtoneAudio) {
      ringtoneAudio.pause();
      ringtoneAudio.currentTime = 0;
    }
  }

  async function answerIncomingCall() {
    if (!incomingCall.value || isAnsweringIncoming.value) return;

    console.log('[useDialer] Answering incoming call:', incomingCall.value.call_id);
    isAnsweringIncoming.value = true;
    error.value = null;
    stopRingtone();

    try {
      // Initialize WebRTC if needed
      const initialized = await initializeWebRTC();
      if (!initialized) {
        throw new Error('Failed to initialize WebRTC');
      }

      // Call backend to answer the PSTN call (plays hold music, conference created on WebRTC join)
      const response = await dialerService.answerIncoming(incomingCall.value.call_id);
      console.log('[useDialer] Backend answered incoming call:', response);

      // Store the call info (conference will be created when WebRTC connects)
      currentCall.value = {
        id: incomingCall.value.call_id,
        officer_id: '',
        tenant_id: '',
        direction: 'inbound',
        status: 'connecting',  // Will become 'connected' when WebRTC is active
        mode: 'webrtc',
        from_number: response.from_number,
        to_number: response.sip_uri,
        started_at: new Date().toISOString(),
        connected_at: null,
        conference_id: response.conference_id,  // Needed for hold/mute controls
      };

      // Add caller as participant using backend response (has correct IDs for controls)
      const participantInfo = response.participant;
      participants.value = [{
        id: participantInfo?.id || incomingCall.value.call_id,
        call_id: incomingCall.value.call_id,
        name: participantInfo?.name || response.from_name || 'Unknown Caller',
        number: participantInfo?.number || response.from_number,
        type: 'external' as const,
        status: 'connected' as const,  // Backend has already connected them
        joined_at: new Date().toISOString(),
        call_control_id: participantInfo?.call_control_id,  // For mute/hold/end controls
      }];
      console.log('[useDialer] Added participant from backend:', participants.value[0]);

      // Make WebRTC call to join the conference that was just created
      const sipUri = webrtcCredentials.value?.sip_uri;
      if (sipUri) {
        console.log('[useDialer] Joining conference via WebRTC:', response.conference_name);
        await telnyxWebRTC.call(sipUri, {
          callerName: webrtcCredentials.value?.caller_name,
          callerNumber: webrtcCredentials.value?.caller_number,
          clientState: {
            call_type: 'inbound_answer',
            initiated_from: 'webrtc_dialer',
            original_call_id: incomingCall.value.call_id,
            conference_id: response.conference_id,
            conference_name: response.conference_name,
          },
        });
      }

      callStatus.value = 'connected';
      officerStatus.value = 'busy';
      incomingCall.value = null;

    } catch (err: any) {
      console.error('[useDialer] Failed to answer incoming call:', err);
      error.value = err.message || 'Failed to answer call';
      incomingCall.value = null;
    } finally {
      isAnsweringIncoming.value = false;
    }
  }

  async function rejectIncomingCall() {
    if (!incomingCall.value) return;

    console.log('[useDialer] Rejecting incoming call:', incomingCall.value.call_id);
    stopRingtone();

    try {
      await dialerService.rejectIncoming(incomingCall.value.call_id);
    } catch (err) {
      console.error('[useDialer] Failed to reject incoming call:', err);
    } finally {
      incomingCall.value = null;
    }
  }

  function dismissIncomingCall() {
    // Just dismiss the UI without rejecting (call will timeout on backend)
    console.log('[useDialer] Dismissing incoming call notification');
    stopRingtone();
    incomingCall.value = null;
  }

  // Timer management
  function startDurationTimer() {
    stopDurationTimer();
    // Only start timer for phone mode, WebRTC timer is handled by useTelnyxWebRTC
    if (connectionMode.value === 'phone') {
      callDuration.value = 0;
      callDurationInterval.value = setInterval(() => {
        callDuration.value++;
      }, 1000);
    }
  }

  function stopDurationTimer() {
    if (callDurationInterval.value) {
      clearInterval(callDurationInterval.value);
      callDurationInterval.value = null;
    }
  }

  // Reset call state
  function resetCallState() {
    stopDurationTimer();
    callStatus.value = 'idle';
    officerStatus.value = 'available';
    currentCall.value = null;
    participants.value = [];
    callDuration.value = 0;
    isMuted.value = false;
    isOnHold.value = false;
    error.value = null;
  }

  // WebSocket event handlers (for both phone and WebRTC modes)
  function handleCallEvent(message: { type: string; payload: Record<string, any> }) {
    const { type, payload } = message;
    console.log('[useDialer] ===== WEBSOCKET EVENT =====');
    console.log('[useDialer] Event type:', type);
    console.log('[useDialer] Payload:', payload);
    console.log('[useDialer] Current connectionMode:', connectionMode.value);
    console.log('[useDialer] Current call before update:', currentCall.value);

    // Handle incoming call events (these come from external callers dialing officer's DID)
    if (type === 'incoming_call') {
      console.log('[useDialer] ===== INCOMING CALL =====');
      console.log('[useDialer] From:', payload.from_number, payload.from_name);
      incomingCall.value = {
        call_id: payload.call_id,
        from_number: payload.from_number,
        from_name: payload.from_name,
        to_number: payload.to_number,
        direction: 'inbound',
      };
      playRingtone();
      return;
    }

    if (type === 'incoming_call_missed' || type === 'incoming_call_ended') {
      console.log('[useDialer] Incoming call ended/missed:', type);
      stopRingtone();

      // Ignore "missed" event if we already answered and are in a call
      // This can happen due to race conditions with timeout tasks
      if (type === 'incoming_call_missed' && currentCall.value?.id === payload.call_id && callStatus.value === 'connected') {
        console.log('[useDialer] Ignoring incoming_call_missed - call was already answered');
        return;
      }

      if (incomingCall.value?.call_id === payload.call_id) {
        incomingCall.value = null;
      }
      // If we were on this call and not connected, reset state
      if (currentCall.value?.id === payload.call_id && callStatus.value !== 'connected') {
        resetCallState();
        loadRecentCalls();
      }
      return;
    }

    if (type === 'incoming_call_answered') {
      console.log('[useDialer] Incoming call answered notification');
      stopRingtone();
      return;
    }

    if (type === 'incoming_call_rejected') {
      console.log('[useDialer] Incoming call rejected');
      stopRingtone();
      incomingCall.value = null;
      return;
    }

    // Handle call disconnected (e.g., caller hung up)
    if (type === 'call.disconnected' || type === 'call_disconnected') {
      console.log('[useDialer] Call disconnected:', payload);
      console.log('[useDialer] Current call:', currentCall.value?.id);
      console.log('[useDialer] Connection mode:', connectionMode.value);

      // Only react to disconnect if it's the officer's own call, not a participant in a hearing
      // Check by call_id match or participant_type === 'officer'
      const isOfficerCall = payload.call_id === currentCall.value?.id ||
                            payload.participant_type === 'officer';

      if (!isOfficerCall) {
        console.log('[useDialer] Ignoring disconnect - not officer call (participant:', payload.participant_name, ')');
        return;
      }

      // End WebRTC call if active
      if (connectionMode.value === 'webrtc') {
        console.log('[useDialer] Hanging up WebRTC call');
        try {
          telnyxWebRTC.hangup();
        } catch (e) {
          console.warn('[useDialer] Error hanging up WebRTC:', e);
        }
      }

      // Always reset call state when we receive disconnect event
      // Don't wait for WebRTC SDK - the backend is authoritative
      console.log('[useDialer] Resetting call state after disconnect');
      resetCallState();
      loadRecentCalls();
      return;
    }

    // For WebRTC mode, we receive events from the backend about the call/conference
    // These events give us the call_id and conference_id needed for controls
    if (connectionMode.value === 'webrtc') {
      console.log('[useDialer] Processing WebRTC event:', type);
      switch (type) {
        case 'call.initiated':
          // Backend has created the call/conference - store the IDs
          console.log('[useDialer] ===== CALL INITIATED =====');
          console.log('[useDialer] call_id:', payload.call_id);
          console.log('[useDialer] conference_id:', payload.conference_id);
          console.log('[useDialer] to_number:', payload.to_number);
          console.log('[useDialer] participants:', payload.participants);
          if (payload.call_id) {
            currentCall.value = {
              id: payload.call_id,
              officer_id: '',
              tenant_id: '',
              direction: 'outbound',
              status: 'initiated',
              mode: 'webrtc',
              from_number: '',
              to_number: payload.to_number || phoneNumber.value,
              started_at: new Date().toISOString(),
              conference_id: payload.conference_id,
            };
            console.log('[useDialer] currentCall.value SET:', currentCall.value);

            // Store participants with real backend IDs
            if (payload.participants && Array.isArray(payload.participants)) {
              // Merge backend participants with any existing local ones (to preserve display info)
              payload.participants.forEach((backendP: any) => {
                const existingIdx = participants.value.findIndex(
                  p => p.number === backendP.number || p.id === backendP.id
                );
                // Map backend's telnyx_call_control_id to frontend's call_control_id
                const callControlId = backendP.call_control_id || backendP.telnyx_call_control_id;
                if (existingIdx >= 0) {
                  // Update existing participant with backend ID
                  participants.value[existingIdx] = {
                    ...participants.value[existingIdx],
                    id: backendP.id,
                    call_control_id: callControlId,
                  };
                } else {
                  // Add new participant from backend
                  participants.value.push({
                    ...backendP,
                    call_control_id: callControlId,
                  });
                }
              });
              console.log('[useDialer] Updated participants with backend IDs:', participants.value);
            }
          }
          break;
        case 'call.answered':
          console.log('[useDialer] ===== CALL ANSWERED =====');
          console.log('[useDialer] payload.call_id:', payload.call_id);
          console.log('[useDialer] payload.participant_id:', payload.participant_id);
          console.log('[useDialer] payload.participant:', payload.participant);
          console.log('[useDialer] current participants:', JSON.stringify(participants.value));

          callStatus.value = 'connected';
          if (currentCall.value && payload.call_id === currentCall.value.id) {
            currentCall.value.status = 'connected';
            currentCall.value.connected_at = new Date().toISOString();
          }
          // Update participant status - try by ID first, then by phone number
          if (payload.participant_id || payload.participant) {
            console.log('[useDialer] Looking for participant by ID:', payload.participant_id);
            let pIdx = participants.value.findIndex(p => p.id === payload.participant_id);
            console.log('[useDialer] Found by ID at index:', pIdx);

            // If not found by ID (might still have temp ID), try matching by phone number
            if (pIdx < 0 && payload.participant?.number) {
              const targetNumber = payload.participant.number.replace(/\D/g, '').slice(-10);
              console.log('[useDialer] Trying to find by phone number:', targetNumber);
              pIdx = participants.value.findIndex(existing => {
                const existingNumber = existing.number.replace(/\D/g, '').slice(-10);
                console.log('[useDialer] Comparing:', existingNumber, 'vs', targetNumber);
                return existingNumber === targetNumber;
              });
              console.log('[useDialer] Found by phone at index:', pIdx);
            }
            if (pIdx >= 0) {
              // Replace the participant object to ensure Vue reactivity
              const updatedParticipant = {
                ...participants.value[pIdx],
                status: 'connected' as const,
              };
              // Also update the ID if we have the real one from backend
              if (payload.participant_id && participants.value[pIdx].id.startsWith('temp-')) {
                updatedParticipant.id = payload.participant_id;
              }
              if (payload.participant?.call_control_id) {
                updatedParticipant.call_control_id = payload.participant.call_control_id;
              }
              participants.value[pIdx] = updatedParticipant;
              console.log('[useDialer] Updated participant to connected:', updatedParticipant);
            } else {
              console.warn('[useDialer] Could not find participant to update! Participant may have wrong ID.');
            }
          } else {
            console.warn('[useDialer] No participant_id or participant in payload - cannot update participant status');
          }
          console.log('[useDialer] WebRTC call answered, participants now:', JSON.stringify(participants.value));
          break;
        case 'call.ended':
          console.log('[useDialer] WebRTC call ended via WebSocket');
          resetCallState();
          loadRecentCalls();
          break;
        case 'call.participant_joined':
        case 'did.call.participant_joined':
          console.log('[useDialer] Participant joined event:', payload);
          if (payload.participant) {
            // Try to find by ID first, then by phone number
            let existingIdx = participants.value.findIndex(p => p.id === payload.participant.id);
            if (existingIdx < 0 && payload.participant.number) {
              const targetNumber = payload.participant.number.replace(/\D/g, '').slice(-10);
              existingIdx = participants.value.findIndex(existing => {
                const existingNumber = existing.number.replace(/\D/g, '').slice(-10);
                return existingNumber === targetNumber;
              });
            }
            if (existingIdx >= 0) {
              // Update existing participant with backend data
              participants.value[existingIdx] = {
                ...participants.value[existingIdx],
                ...payload.participant,
                status: payload.participant.status || 'connected',
              };
              console.log('[useDialer] Updated participant:', participants.value[existingIdx]);
            } else {
              participants.value.push(payload.participant);
              console.log('[useDialer] Added new participant:', payload.participant);
            }
          }
          break;
        case 'call.participant_left':
        case 'did.call.participant_left':
          console.log('[useDialer] Participant left event:', payload);
          if (payload.participant_id) {
            const idx = participants.value.findIndex(p => p.id === payload.participant_id);
            if (idx >= 0) {
              participants.value[idx] = { ...participants.value[idx], status: 'disconnected' };
              console.log('[useDialer] Marked participant as disconnected:', payload.participant_id);
            }
          }
          break;
        case 'did.call.participant_muted':
        case 'did.call.participant_unmuted':
          if (payload.participant_id) {
            const idx = participants.value.findIndex(p => p.id === payload.participant_id);
            if (idx >= 0) {
              participants.value[idx] = { ...participants.value[idx], is_muted: payload.is_muted };
            }
          }
          break;
        case 'did.call.participant_held':
        case 'did.call.participant_resumed':
          if (payload.participant_id) {
            const idx = participants.value.findIndex(p => p.id === payload.participant_id);
            if (idx >= 0) {
              participants.value[idx] = { ...participants.value[idx], is_on_hold: payload.is_on_hold };
            }
          }
          break;
      }
      return;
    }

    // Phone mode handling
    console.log('[useDialer] Processing Phone mode event:', type);
    switch (type) {
      case 'call.initiated':
        console.log('[useDialer] Phone mode call.initiated');
        if (payload.call_id) {
          currentCall.value = {
            id: payload.call_id,
            officer_id: '',
            tenant_id: '',
            direction: 'outbound',
            status: payload.status || 'initiated',
            mode: 'phone',
            from_number: '',
            to_number: payload.to_number || phoneNumber.value,
            started_at: new Date().toISOString(),
          };
          console.log('[useDialer] Phone mode currentCall set:', currentCall.value);
        }
        // Add participant if provided
        if (payload.participant) {
          participants.value.push(payload.participant);
          console.log('[useDialer] Phone mode participant added:', payload.participant);
        }
        break;
      case 'call.ringing':
        callStatus.value = 'ringing';
        break;
      case 'call.answered':
        console.log('[useDialer] Phone mode call.answered');
        console.log('[useDialer] payload:', payload);
        console.log('[useDialer] current participants:', JSON.stringify(participants.value));

        // Update call status
        if (payload.status === 'calling_target') {
          // Officer answered, target is being dialed
          callStatus.value = 'calling';
          console.log('[useDialer] Officer answered, calling target...');
        } else {
          callStatus.value = 'connected';
        }

        if (currentCall.value && payload.call_id === currentCall.value.id) {
          currentCall.value.status = payload.status || 'connected';
          if (payload.status === 'connected') {
            currentCall.value.connected_at = new Date().toISOString();
          }
        }

        // Add new participant if provided (target being dialed)
        if (payload.participant && payload.status === 'calling_target') {
          const existingIdx = participants.value.findIndex(p => p.id === payload.participant.id);
          if (existingIdx < 0) {
            participants.value.push(payload.participant);
            console.log('[useDialer] Phone mode: Added target participant:', payload.participant);
          }
        }

        // Update participant status if this is participant answering
        if (payload.participant_id || payload.participant) {
          let pIdx = participants.value.findIndex(p => p.id === payload.participant_id);

          // Try matching by phone number if not found by ID
          if (pIdx < 0 && payload.participant?.number) {
            const targetNumber = payload.participant.number.replace(/\D/g, '').slice(-10);
            pIdx = participants.value.findIndex(existing => {
              const existingNumber = existing.number.replace(/\D/g, '').slice(-10);
              return existingNumber === targetNumber;
            });
          }

          if (pIdx >= 0 && payload.status !== 'calling_target') {
            // Update participant to connected
            participants.value[pIdx] = {
              ...participants.value[pIdx],
              status: 'connected',
              ...(payload.participant || {}),
            };
            console.log('[useDialer] Phone mode: Updated participant to connected:', participants.value[pIdx]);
          }
        }
        break;
      case 'call.ended':
      case 'call.disconnected':
        console.log('[useDialer] Phone mode call ended');
        resetCallState();
        loadRecentCalls();
        break;
      case 'call.participant_joined':
      case 'did.call.participant_joined':
        console.log('[useDialer] Phone mode - Participant joined event:', payload);
        if (payload.participant) {
          // Try to find by ID first, then by phone number
          let existingIdx = participants.value.findIndex(p => p.id === payload.participant.id);
          if (existingIdx < 0 && payload.participant.number) {
            const targetNumber = payload.participant.number.replace(/\D/g, '').slice(-10);
            existingIdx = participants.value.findIndex(existing => {
              const existingNumber = existing.number.replace(/\D/g, '').slice(-10);
              return existingNumber === targetNumber;
            });
          }
          if (existingIdx >= 0) {
            // Update existing participant with backend data
            participants.value[existingIdx] = {
              ...participants.value[existingIdx],
              ...payload.participant,
              status: payload.participant.status || 'connected',
            };
            console.log('[useDialer] Phone mode - Updated participant:', participants.value[existingIdx]);
          } else {
            participants.value.push(payload.participant);
            console.log('[useDialer] Phone mode - Added new participant:', payload.participant);
          }
        }
        break;
      case 'call.participant_left':
      case 'did.call.participant_left':
        console.log('[useDialer] Phone mode - Participant left event:', payload);
        if (payload.participant_id) {
          const idx = participants.value.findIndex(p => p.id === payload.participant_id);
          if (idx >= 0) {
            participants.value[idx] = { ...participants.value[idx], status: 'disconnected' };
            console.log('[useDialer] Phone mode - Marked participant as disconnected:', payload.participant_id);
          }
        }
        break;
      case 'did.call.participant_muted':
      case 'did.call.participant_unmuted':
        if (payload.participant_id) {
          const idx = participants.value.findIndex(p => p.id === payload.participant_id);
          if (idx >= 0) {
            participants.value[idx] = { ...participants.value[idx], is_muted: payload.is_muted };
          }
        }
        break;
      case 'did.call.participant_held':
      case 'did.call.participant_resumed':
        if (payload.participant_id) {
          const idx = participants.value.findIndex(p => p.id === payload.participant_id);
          if (idx >= 0) {
            participants.value[idx] = { ...participants.value[idx], is_on_hold: payload.is_on_hold };
          }
        }
        break;
    }
  }

  // Load DID settings (including ringtone URL) from tenant
  // Uses the /tenant/did endpoint which is accessible to all users (including officers)
  async function loadDIDSettings() {
    try {
      const didSettings = await settingsService.getTenantDIDSettings();
      if (didSettings?.ringtoneUrl) {
        ringtoneUrl.value = didSettings.ringtoneUrl;
        console.log('[useDialer] Loaded tenant ringtone URL:', ringtoneUrl.value);
      } else {
        console.log('[useDialer] No tenant ringtone configured, using default');
      }
    } catch (err) {
      console.warn('[useDialer] Failed to load DID settings, using default ringtone:', err);
    }
  }

  // Initialize
  function initialize() {
    console.log('[useDialer] ===== INITIALIZING =====');
    console.log('[useDialer] Loading recent calls, quick dial contacts, and DID settings...');
    loadRecentCalls();
    loadQuickDialContacts();
    loadDIDSettings();

    // Subscribe to call events for both phone and WebRTC modes (telephony channel)
    console.log('[useDialer] Subscribing to telephony channel...');
    unsubscribeCall = onChannel('telephony', handleCallEvent);
    console.log('[useDialer] Subscribed to telephony channel. Ready to receive call events.');
    console.log('[useDialer] WebSocket connected:', wsConnected.value);
  }

  // Cleanup
  function cleanup() {
    resetCallState();
    if (unsubscribeCall) {
      unsubscribeCall();
      unsubscribeCall = null;
    }

    // Disconnect WebRTC if connected
    if (telnyxWebRTC.state.value.isInitialized) {
      telnyxWebRTC.disconnect();
    }
  }

  onMounted(() => {
    initialize();
  });

  onUnmounted(() => {
    cleanup();
  });

  return {
    // State
    phoneNumber,
    callStatus,
    connectionMode,
    officerStatus,
    forwardToNumber,
    currentCall,
    participants,
    callDuration,
    isMuted,
    isOnHold,
    recentCalls,
    quickDialContacts,
    isLoading,
    isLoadingRecent,
    isLoadingQuickDial,
    isWebRTCInitializing,
    error,
    isConnected: wsConnected,

    // Incoming call state
    incomingCall,
    isAnsweringIncoming,

    // Audio device state (from Telnyx WebRTC)
    audioInputDevices: telnyxWebRTC.audioInputDevices,
    audioOutputDevices: telnyxWebRTC.audioOutputDevices,
    selectedAudioInput: telnyxWebRTC.selectedAudioInput,
    selectedAudioOutput: telnyxWebRTC.selectedAudioOutput,
    isLoadingAudioDevices: telnyxWebRTC.isLoadingDevices,

    // Computed
    isInCall,
    hasIncomingCall,
    formattedDuration,
    canMakeCall,
    isWebRTCReady,

    // Methods
    formatPhoneNumber,
    inputDigit,
    backspace,
    clearNumber,
    sendDTMF,
    initiateCall,
    endCall,
    toggleMute,
    toggleHold,
    transferCall,
    addParticipant,
    removeParticipant,
    loadRecentCalls,
    loadQuickDialContacts,
    addToQuickDial,
    removeFromQuickDial,
    initializeWebRTC,
    initialize,
    cleanup,

    // Incoming call methods
    answerIncomingCall,
    rejectIncomingCall,
    dismissIncomingCall,

    // Audio device methods
    loadAudioDevices: telnyxWebRTC.loadAudioDevices,
    setAudioInputDevice: telnyxWebRTC.setAudioInputDevice,
    setAudioOutputDevice: telnyxWebRTC.setAudioOutputDevice,
  };
}
